import React, { useState, useMemo, useRef, useEffect } from 'react'

import { SegmentedControl } from '@UILibrary/SegmentedControl'
import { Text } from '@UILibrary/Text'

import { useOrganizationDashboardQuery } from '@api/useOrganizationDashboardQuery'

import { track } from '@utils/tracking'

import { EngagementFeedback } from './components/EngagementFeedback'
import { Overview } from './components/Overview'
import { FinancialHealth } from './components/FinancialHealth'

import { Stack } from '@UILibrary/Stack'

import styles from './dashboard.module.sass'


export const Dashboard = ({ organization, initialData, incognito = false }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const segmentedControlRef = useRef(null)
  const [isSticky, setIsSticky] = useState(false)

  let query = useOrganizationDashboardQuery(selectedPeriod, organization.id)
  let data = initialData

  if (query.status === 'success') {
    data = query.data
  }

  useMemo(() => {
    track('Organization Report: TimeScale', { selectedPeriod: selectedPeriod })
  }, [selectedPeriod])

  const segmentOptions = [
    {
      label: 'Week',
      value: 'week',
    },
    {
      label: 'Month',
      value: 'month',
    },
  ]

  if (new Date(organization.createdAt).getFullYear() > new Date().getFullYear()) {
    segmentOptions.push({
      label: 'Year',
      value: 'year',
    })
  }

  useEffect(() => {
    if (!segmentedControlRef.current) return

    const initialOffsetTop = segmentedControlRef.current.offsetTop

    const handleScroll = () => {
      const scrollPosition = window.pageYOffset

      if (scrollPosition >= initialOffsetTop) {
        setIsSticky(true)
      } else {
        setIsSticky(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const queryData = data.data.attributes

  return (
    <div className={styles.organizationDashboardLayout}>
      {!incognito && (
        <>
          <h1>{organization.name}'s Report</h1>
          <p>Report refreshed {queryData.refreshedAt} ago</p>
        </>
      )}

      <div className={styles.detailsSectionHeader}>
        <Text heading2b as="h1" colour="default" margin='none'>
          Financial Freedom Programme overview
        </Text>

        <div
          ref={segmentedControlRef}
          className={`${styles.segmentedControlContainer} ${isSticky ? styles.sticky : ''}`}
        >
          <SegmentedControl
            className={styles.segmentedControl}
            selected_default={selectedPeriod}
            segments={segmentOptions}
            onChange={setSelectedPeriod}
          />
        </div>
      </div>

      <Stack vertical spacing="xl">
        <Overview
          financialHealthData={{
            average_start_score: queryData.overview.financial_health.average_start_score,
            average_current_score: queryData.overview.financial_health.average_current_score,
            score_improvement_percentage: queryData.overview.financial_health.score_improvement_percentage,
          }}
          programFeedback={queryData.overview.program_feedback}
          programEngagementBreakdown={queryData.overview.program_engagement_breakdown}
        />

        <FinancialHealth
          financial_health={queryData.financialHealth}
          selectedPeriod={selectedPeriod}
        />

        <EngagementFeedback
          engagementFeedback={queryData.engagementFeedback}
          organizationName={queryData.organization.name}
        />
      </Stack>

      <p>
        Reporting is for illustrative purposes only and do not constitute any guarantees as it is based on the data captured and a projection forward. The reporting of aggregate retirement savings is based on the best estimate net investment returns for the relevant cohort. The projections use current assumptions based on extensive historical data to calculate estimated invested returns. The purpose of the reporting is for the employer to better understand their employee financial health based on industry standard. We depend on third parties to provide information and although reasonable steps have been taken to ensure the validity and accuracy of the information, we do not accept responsibility for any claim based on these projections.
      </p>

    </div>
  )
}
